import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowDown, Volume2, VolumeX } from "lucide-react";
import SectionLabel from "./SectionLabel";

const HERO_WORDS = ["Branding.", "Design.", "Execution.", "Delivered"];

const HeroSystemBuild = () => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !v.muted;
    v.muted = next;
    setMuted(next);
    if (!next) {
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
  };

  return (
    <section
      data-testid="hero-system-build"
      className="relative min-h-[100svh] pt-32 pb-24 overflow-hidden"
    >
      {/* Architectural overlay grid */}
      <div className="pointer-events-none absolute inset-0 architectural-grid opacity-60" />
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-0 right-0 top-1/2 h-px bg-black/20" />
        <div className="absolute top-0 bottom-0 left-1/2 w-px bg-black/20" />
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* LEFT - system assembling visual */}
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative w-full aspect-square max-w-[460px] mx-auto">
              {/* Crosshair guides */}
              <div className="absolute inset-0 border border-black/30" />
              <div className="absolute left-0 right-0 top-1/2 h-px bg-black/25" />
              <div className="absolute top-0 bottom-0 left-1/2 w-px bg-black/25" />
              <div className="absolute top-3 left-3 mono-label z-10">SYSTEM / BUILD</div>
              <div className="absolute bottom-3 right-3 mono-label z-10">STAGE 03</div>

              {/* Animated UPRCSE logo film */}
              <motion.video
                ref={videoRef}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.9, ease: [0.22, 0.61, 0.36, 1] }}
                src="/assets/videos/UprcseLogoAnimated.mp4"
                autoPlay
                loop
                muted={muted}
                playsInline
                preload="auto"
                aria-label="UPRCSE logo build animation"
                className="absolute inset-0 w-full h-full object-contain"
                data-testid="hero-logo-video"
              />

              {/* Mute / Unmute toggle */}
              <button
                type="button"
                onClick={toggleMute}
                aria-label={muted ? "Unmute logo film" : "Mute logo film"}
                data-testid="hero-mute-toggle"
                className="absolute bottom-3 left-3 z-10 flex items-center gap-2 px-3 py-1.5 border border-black/45 bg-white/70 backdrop-blur-sm hover:border-[#AE519F] hover:text-[#AE519F] text-black transition-colors"
              >
                {muted ? (
                  <VolumeX size={14} strokeWidth={1.5} />
                ) : (
                  <Volume2 size={14} strokeWidth={1.5} />
                )}
                <span className="mono-label" style={{ color: "inherit" }}>
                  {muted ? "UNMUTE" : "MUTE"}
                </span>
              </button>
            </div>
          </div>

          {/* RIGHT - headline */}
          <div className="lg:col-span-7 order-1 lg:order-2">
            <SectionLabel className="mb-6" testid="label-hero">
              [ 00 ] - A FULL-STACK BRAND EXECUTION PARTNER
            </SectionLabel>

            <h1
              className="font-archivo font-black text-black h-tighter text-5xl md:text-6xl lg:text-7xl"
              style={{ fontFamily: "Archivo, sans-serif" }}
              data-testid="hero-heading"
            >
              {HERO_WORDS.map((w, i) => (
                <span key={w + i} className="inline-block overflow-hidden align-bottom mr-3">
                  <motion.span
                    initial={{ y: "120%", opacity: 0 }}
                    animate={{ y: "0%", opacity: 1 }}
                    transition={{
                      duration: 0.85,
                      delay: 0.35 + i * 0.13,
                      ease: [0.22, 0.61, 0.36, 1],
                    }}
                    className="inline-block"
                  >
                    {w}
                  </motion.span>
                </span>
              ))}
              <span className="inline-block overflow-hidden align-bottom">
                <motion.span
                  initial={{ y: "120%", opacity: 0 }}
                  animate={{ y: "0%", opacity: 1 }}
                  transition={{
                    duration: 0.85,
                    delay: 1.6,
                    ease: [0.22, 0.61, 0.36, 1],
                  }}
                  className="inline-block text-[#AE519F]"
                >
                  End-to-End.
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.9 }}
              className="mt-8 max-w-xl text-black/75 text-base md:text-lg leading-relaxed"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              UPRCSE is a branding and design house that builds, executes and
              manages complete brand ecosystems - from identity to on-ground
              presence. One team. One system. Across every touchpoint.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 2.05 }}
              className="mt-10 flex flex-wrap items-center gap-5"
            >
              <a href="/connect" className="liquid-btn is-primary" data-testid="hero-cta-primary">
                Start a Conversation
              </a>
              <a href="#manifesto" className="liquid-btn" data-testid="hero-cta-secondary">
                See the System
              </a>
            </motion.div>
          </div>
        </div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.3 }}
          className="hidden md:flex items-center gap-3 mt-24"
        >
          <ArrowDown className="text-black/70" size={16} />
          <span className="mono-label">SCROLL TO ASSEMBLE</span>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSystemBuild;
